// src/components/ErrorBoundary.tsx
import React, { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(_error: Error): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({
      error,
      errorInfo,
    });

    // Log to error tracking service (Sentry, etc.)
    console.error('Error caught by boundary:', error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-black flex items-center justify-center p-4">
          <div className="max-w-md w-full">
            {/* Error Card */}
            <div className="bg-gradient-to-br from-red-900/30 via-gray-900/50 to-black border border-red-500/30 rounded-xl p-8 text-center">
              {/* Icon */}
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="text-red-400" size={40} />
              </div>

              {/* Title */}
              <h2 className="text-2xl font-bold text-red-300 mb-2">
                Something Went Wrong
              </h2>

              {/* Description */}
              <p className="text-gray-400 mb-6">
                The application encountered an unexpected error. Your data is safe and
                stored locally - nothing has been lost.
              </p>

              {/* Error Details (in dev mode) */}
              {import.meta.env.DEV && this.state.error && (
                <details className="text-left mb-6 p-4 bg-black/30 rounded border border-red-500/20">
                  <summary className="text-red-400 text-sm font-mono cursor-pointer mb-2">
                    Error Details (Dev Mode)
                  </summary>
                  <pre className="text-xs text-gray-500 overflow-auto max-h-40">
                    {this.state.error.toString()}
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </details>
              )}

              {/* Actions */}
              <div className="space-y-3">
                <button
                  onClick={this.handleReset}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-500 text-white font-semibold rounded-lg transition"
                >
                  <RefreshCw size={20} />
                  Try Again
                </button>

                <button
                  onClick={() => window.location.reload()}
                  className="w-full px-6 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 font-semibold rounded-lg transition"
                >
                  Reload Page
                </button>
              </div>

              {/* Support Link */}
              <p className="mt-6 text-sm text-gray-500">
                If this persists,{' '}
                <a
                  href="https://github.com/postrv/avent-horizon/issues"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-red-400 hover:text-red-300 underline"
                >
                  report the issue
                </a>
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
